/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: FirewallSettingsPage.tsx
 🗓️ Created: 2025-12-06
 🧩 Description:
    Firewall Settings page - Configure firewall and intrusion detection
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useEffect } from 'react';
import { Shield, Loader2, Network, ShieldCheck } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useFirewallSettings } from './hooks/useFirewallSettings';
import { ModuleFormHeader } from '../../components/layouts';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Toggle } from '../../components/ui/Toggle';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { FormBox } from '../../components/ui/FormBox';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import type { FirewallSettingsFormData } from './types';

export default function FirewallSettingsPage() {
  const { t } = useI18n();
  const {
    settings,
    serviceStatuses,
    loading,
    saving,
    saveSettings,
    toggleFirewall,
    toggleIntrusionDetection,
  } = useFirewallSettings();
  const [formData, setFormData] = useState<FirewallSettingsFormData>({});

  // Initialize form data when settings load
  useEffect(() => {
    if (settings) {
      setFormData({
        firewallEnabled: settings.firewallEnabled,
        intrusionDetectionEnabled: settings.intrusionDetectionEnabled,
        failedAttemptsAllowed: settings.failedAttemptsAllowed,
        findTime: settings.findTime,
        banTime: settings.banTime,
        notificationEmail: settings.notificationEmail,
      });
    }
  }, [settings]);

  const handleSubmit = useCallback(async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    try {
      // Only save configuration settings, not service states
      const { firewallEnabled, intrusionDetectionEnabled, ...configData } = formData;
      await saveSettings(configData);
    } catch (error) {
      // Error already handled in hook
    }
  }, [formData, saveSettings]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChange = useCallback((field: keyof FirewallSettingsFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleFirewallToggle = useCallback(async (enabled: boolean) => {
    try {
      await toggleFirewall(enabled);
    } catch (error) {
      // Error already handled in hook
    }
  }, [toggleFirewall]);

  const handleIntrusionDetectionToggle = useCallback(async (enabled: boolean) => {
    try {
      await toggleIntrusionDetection(enabled);
    } catch (error) {
      // Error already handled in hook
    }
  }, [toggleIntrusionDetection]);

  // Convert seconds to minutes for display
  const secondsToMinutes = (seconds: number) => Math.floor(seconds / 60);
  const minutesToSeconds = (minutes: number) => minutes * 60;

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 pb-20">
      <ModuleFormHeader
        icon={<Shield className="h-5 w-5 text-primary" />}
        title={t('firewallSettings.title')}
        backButtonText=""
        onBack={() => {}}
        hideBackButton={true}
      />

      <div className="mt-8">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Firewall Enabled */}
          <FormBox
            title={t('firewallSettings.firewallEnabled')}
            icon={<Network className="h-5 w-5" />}
            description={t('firewallSettings.firewallEnabledDescription')}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
              <SimpleTooltip 
                content={t('firewallSettings.firewallEnabledTooltip')}
                position="top"
                maxWidth="300px"
              >
                <Label htmlFor="firewallEnabled" className="text-left">
                  {t('firewallSettings.firewallEnabled')}
                </Label>
              </SimpleTooltip>
              <div className="col-span-3">
                <Toggle
                  id="firewallEnabled"
                  value={serviceStatuses?.firewallEnabled ?? false}
                  onChange={handleFirewallToggle}
                  disabled={saving}
                />
              </div>
            </div>
          </FormBox>

          {/* Intrusion Detection */}
          <FormBox
            title={t('firewallSettings.intrusionDetectionEnabled')}
            icon={<ShieldCheck className="h-5 w-5" />}
            description={t('firewallSettings.intrusionDetectionEnabledDescription')}
          >
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                <SimpleTooltip 
                  content={t('firewallSettings.intrusionDetectionEnabledTooltip')}
                  position="top"
                  maxWidth="300px"
                >
                  <Label htmlFor="intrusionDetectionEnabled" className="text-left">
                    {t('firewallSettings.intrusionDetectionEnabled')}
                  </Label>
                </SimpleTooltip>
                <div className="col-span-3">
                  <Toggle
                    id="intrusionDetectionEnabled"
                    value={serviceStatuses?.intrusionDetectionEnabled ?? false}
                    onChange={handleIntrusionDetectionToggle}
                    disabled={saving}
                  />
                </div>
              </div>

              {/* Intrusion Detection Settings */}
              {serviceStatuses?.intrusionDetectionEnabled ? (
                <div className="pt-4 border-t">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                    {/* Row 1: Failed Attempts Allowed | Find Time Window */}
                    <SimpleTooltip 
                      content={t('firewallSettings.failedAttemptsAllowedTooltip')}
                      position="top"
                      maxWidth="300px"
                    >
                      <Label htmlFor="failedAttempts" className="text-left">
                        {t('firewallSettings.failedAttemptsAllowed')}
                      </Label>
                    </SimpleTooltip>
                    <Input
                      id="failedAttempts"
                      type="number"
                      min="1"
                      max="20"
                      value={formData.failedAttemptsAllowed ?? settings?.failedAttemptsAllowed ?? 5}
                      onChange={(e) => handleChange('failedAttemptsAllowed', parseInt(e.target.value, 10))}
                    />

                    <SimpleTooltip 
                      content={t('firewallSettings.findTimeTooltip')}
                      position="top"
                      maxWidth="300px"
                    >
                      <Label htmlFor="findTime" className="text-left">
                        {t('firewallSettings.findTime')} ({t('firewallSettings.minutes')})
                      </Label>
                    </SimpleTooltip>
                    <Input
                      id="findTime"
                      type="number"
                      min="1"
                      max="1440"
                      value={secondsToMinutes(formData.findTime ?? settings?.findTime ?? 600)}
                      onChange={(e) => handleChange('findTime', minutesToSeconds(parseInt(e.target.value, 10)))}
                    />

                    {/* Row 2: Ban Duration | Notification Email */}
                    <SimpleTooltip 
                      content={t('firewallSettings.banTimeTooltip')}
                      position="top"
                      maxWidth="300px"
                    >
                      <Label htmlFor="banTime" className="text-left">
                        {t('firewallSettings.banTime')} ({t('firewallSettings.minutes')})
                      </Label>
                    </SimpleTooltip>
                    <Input
                      id="banTime"
                      type="number"
                      min="1"
                      max="10080"
                      value={secondsToMinutes(formData.banTime ?? settings?.banTime ?? 3600)}
                      onChange={(e) => handleChange('banTime', minutesToSeconds(parseInt(e.target.value, 10)))}
                    />

                    <SimpleTooltip 
                      content={t('firewallSettings.notificationEmailTooltip')}
                      position="top"
                      maxWidth="300px"
                    >
                      <Label htmlFor="notificationEmail" className="text-left">
                        {t('firewallSettings.notificationEmail')}
                      </Label>
                    </SimpleTooltip>
                    <Input
                      id="notificationEmail"
                      type="email"
                      placeholder={t('firewallSettings.notificationEmailPlaceholder')}
                      value={formData.notificationEmail ?? settings?.notificationEmail ?? ''}
                      onChange={(e) => handleChange('notificationEmail', e.target.value || null)}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </FormBox>
        </form>
      </div>

      <FixedActionBar
        onSave={handleSubmit}
        saveText={t('common.save')}
        isSaving={saving}
        saveDisabled={saving}
        showCancel={false}
        showDelete={false}
      />
    </div>
  );
}
